import { useState, useEffect } from 'react';
import { FaUpload, FaTrash, FaImage, FaSpinner, FaPlus, FaVolumeUp, FaEye, FaTimes, FaCamera } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { extractTextFromImage, generateSentencesFromText } from '../../../utilities/extensions/imageToText';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const WordMeaningActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [{
      languageWord: '', 
      languageMeaning: '', 
      englishMeaning: '', 
      languageWordAudio: null,
      languageMeaningAudio: null,
      englishMeaningAudio: null,
      image: null,
      confusingLetters: []
    }]
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  
  // Image-to-text popup state
  const [showImageToTextPopup, setShowImageToTextPopup] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [extractedText, setExtractedText] = useState(''); // eslint-disable-line no-unused-vars
  const [generatedWords, setGeneratedWords] = useState([]);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Build storage path using the helper
  const buildStoragePath = ({
    wordId,
    fileName
  }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;
    
    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'word_meaning',
      fileName,
      itemId: wordId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent(parsed);
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    // Notify parent component of content changes
    onContentChange(content);
  }, [content, onContentChange]);

  const addWord = () => {
    const newWords = [...content.words, { 
      languageWord: '', 
      languageMeaning: '', 
      englishMeaning: '', 
      languageWordAudio: null,
      languageMeaningAudio: null,
      englishMeaningAudio: null,
      image: null,
      confusingLetters: []
    }];
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const removeWord = async (index) => {
    if (content.words.length > 1) {
      const wordToDelete = content.words[index];
      const storage = getStorage();
      
      // Delete all associated files from Firebase Storage
      const fileTypes = ['languageWordAudio', 'languageMeaningAudio', 'englishMeaningAudio', 'image'];
      
      for (const fileType of fileTypes) {
        const filePath = wordToDelete[fileType];
        if (filePath) {
          try {
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
          } catch (error) {

            // Continue with deletion even if some files fail to delete
          }
        }
      }
      
      // Remove word from local state
      const newWords = content.words.filter((_, i) => i !== index);
      setContent({ ...content, words: newWords });
    }
  };

  const updateWord = (index, field, value) => {
    const newWords = [...content.words];
    newWords[index] = { ...newWords[index], [field]: value };
    setContent({ ...content, words: newWords });
  };


  // File upload handlers
  const handleFileUpload = async (index, fileType, file) => {
    if (!file || !chapterId || !grade) {
      return;
    }

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      
      // Check if there's an existing file for this field and delete it
      const word = content.words[index];
      const existingFilePath = word[fileType];
      if (existingFilePath) {
        try {
          const existingFileRef = ref(storage, existingFilePath);
          await deleteObject(existingFileRef);
        } catch (deleteError) {

          // Continue with upload even if deletion fails
        }
      }
      
      // Generate storage path using the helper
      const storagePath = buildStoragePath({
        wordId: `word${index + 1}`,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);

      // Upload file to Firebase Storage
      await uploadBytes(storageRef, file);

      // Update content with just the storage path
      const newWords = [...content.words];
      newWords[index] = { 
        ...newWords[index], 
        [fileType]: storagePath // Store only the path
      };
      setContent({ ...content, words: newWords });
    } catch (error) {

      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = async (index, fileType) => {
    const word = content.words[index];
    const filePath = word[fileType];
    
    // Delete from Firebase Storage if path exists
    if (filePath) {
      try {
        const storage = getStorage();
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
      } catch (error) {

        // Continue with local removal even if storage deletion fails
      }
    }

    // Remove from local state
    const newWords = [...content.words];
    newWords[index] = { ...newWords[index], [fileType]: null };
    setContent({ ...content, words: newWords });
  };

  const playAudio = async (audioPath) => {
    if (audioPath) {
      try {
        const storage = getStorage();
        const audioRef = ref(storage, audioPath);
        const downloadURL = await getDownloadURL(audioRef);
        const audio = new Audio(downloadURL);
        audio.play().catch(() => {});
      } catch (error) {


        alert(`Failed to play audio. Error: ${error.message}\nPath: ${audioPath}`);
      }
    }
  };

  // Image-to-text functions
  const handleImageUploadForText = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImage(file);
    
    // Create blob URL for preview
    const blobUrl = URL.createObjectURL(file);
    setImageBlobUrl(blobUrl);
    
    setImageProcessing(true);
    setExtractedText('');
    setGeneratedWords([]);

    try {
      // Extract text from image
      const storage = getStorage();
      const text = await extractTextFromImage(file, storage, db);
      setExtractedText(text);
      
      // Generate word pairs from extracted text
      const sentences = generateSentencesFromText(text);
      
      // Convert sentences to word meaning format
      const words = sentences.map(sentence => {
        const languageText = sentence.languageText || '';
        let languageWord = '';
        let languageMeaning = '';
        
        // Split by hyphen if it exists
        if (languageText.includes('-')) {
          const parts = languageText.split('-');
          languageWord = parts[0].trim();
          languageMeaning = parts.slice(1).join('-').trim(); // Join remaining parts in case of multiple hyphens
        } else {
          languageWord = languageText;
        }
        
        return {
          languageWord,
          languageMeaning,
          englishMeaning: sentence.englishText || '',
          languageWordAudio: null,
          languageMeaningAudio: null,
          englishMeaningAudio: null,
          image: null,
          confusingLetters: []
        };
      });
      
      setGeneratedWords(words);
    } catch (error) {

      alert('Failed to process image: ' + error.message);
    } finally {
      setImageProcessing(false);
    }
  };

  const addGeneratedWords = () => {
    if (generatedWords.length > 0) {
      const newWords = [...content.words, ...generatedWords];
      setContent({ ...content, words: newWords });
      closeImageToTextPopup();
    }
  };

  const closeImageToTextPopup = () => {
    setShowImageToTextPopup(false);
    setUploadedImage(null);
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
      setImageBlobUrl(null);
    }
    setExtractedText('');
    setGeneratedWords([]);
    setImageProcessing(false);
  };

  const editGeneratedWord = (index, field, value) => {
    const updated = [...generatedWords];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedWords(updated);
  };

  const removeGeneratedWord = (index) => {
    const updated = generatedWords.filter((_, i) => i !== index);
    setGeneratedWords(updated);
  };

  const addRowAbove = (index) => {
    const newWord = {
      languageWord: '',
      languageMeaning: '',
      englishMeaning: '',
      languageWordAudio: null,
      languageMeaningAudio: null,
      englishMeaningAudio: null,
      image: null,
      confusingLetters: []
    };
    const updated = [...generatedWords];
    updated.splice(index, 0, newWord);
    setGeneratedWords(updated);
  };

  // Generate audio for generated words
  const generateAudioForWords = async () => {
    if (generatedWords.length === 0) return;

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const ttsRequests = [];
      
      generatedWords.forEach((word, index) => {
        // Add Language word audio request
        if (word.languageWord && word.languageWord.trim()) {
          ttsRequests.push({
            text: word.languageWord,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'languageWord'
          });
        }
        
        // Add Language meaning audio request
        if (word.languageMeaning && word.languageMeaning.trim()) {
          ttsRequests.push({
            text: word.languageMeaning,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'languageMeaning'
          });
        }
        
        // Add English meaning audio request
        if (word.englishMeaning && word.englishMeaning.trim()) {
          ttsRequests.push({
            text: word.englishMeaning,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            type: 'englishMeaning'
          });
        }
      });

      // Generate audio for all requests
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );
      
      // Map audio URLs back to words
      const updatedWords = [...generatedWords];
      
      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          const { wordIndex, type } = request;
          if (type === 'languageWord') {
            updatedWords[wordIndex].languageWordAudio = audioUrl;
          } else if (type === 'languageMeaning') {
            updatedWords[wordIndex].languageMeaningAudio = audioUrl;
          } else if (type === 'englishMeaning') {
            updatedWords[wordIndex].englishMeaningAudio = audioUrl;
          }
        }
      });
      
      setGeneratedWords(updatedWords);
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Generate audio for existing words (content.words) that are missing audio
  const generateAudioForExistingWords = async () => {
    if (!content.words || content.words.length === 0) {
      alert('No words available for audio generation');
      return;
    }

    const ttsRequests = [];

    content.words.forEach((word, index) => {
      // Language word audio missing
      if (
        word.languageWord &&
        word.languageWord.trim() &&
        !word.languageWordAudio
      ) {
        ttsRequests.push({
          text: word.languageWord,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'languageWord',
        });
      }

      // Language meaning audio missing
      if (
        word.languageMeaning &&
        word.languageMeaning.trim() &&
        !word.languageMeaningAudio
      ) {
        ttsRequests.push({
          text: word.languageMeaning,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'languageMeaning',
        });
      }

      // English meaning audio missing
      if (
        word.englishMeaning &&
        word.englishMeaning.trim() &&
        !word.englishMeaningAudio
      ) {
        ttsRequests.push({
          text: word.englishMeaning,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          wordIndex: index,
          type: 'englishMeaning',
        });
      }
    });

    if (ttsRequests.length === 0) {
      alert('All words already have audio for the available texts');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map((req) => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender,
        })),
        db,
        storage
      );

      const updatedWords = [...content.words];

      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (!audioUrl || audioUrl.startsWith('[TTS Error:')) return;

        const { wordIndex, type } = request;
        if (type === 'languageWord') {
          updatedWords[wordIndex].languageWordAudio = audioUrl;
        } else if (type === 'languageMeaning') {
          updatedWords[wordIndex].languageMeaningAudio = audioUrl;
        } else if (type === 'englishMeaning') {
          updatedWords[wordIndex].englishMeaningAudio = audioUrl;
        }
      });

      setContent((prev) => ({ ...prev, words: updatedWords }));
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Translate Language words to English for meanings
  const translateLanguageWords = async () => {
    if (generatedWords.length === 0) return;

    setTranslating(true);

    try {
      // Extract Language words that don't have English meanings
      const wordsToTranslate = generatedWords
        .map((word, index) => ({ text: word.languageWord, index }))
        .filter(item => item.text && item.text.trim() && !generatedWords[item.index].englishMeaning);

      if (wordsToTranslate.length === 0) {
        alert('All words already have English meanings or no Language words to translate.');
        return;
      }

      // Translate the Language words (Kannada to English)
      const textsToTranslate = wordsToTranslate.map(item => item.text);
      const translatedTexts = await translateBatchToText(
        textsToTranslate, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      
      // Update words with English meanings
      const updatedWords = [...generatedWords];
      wordsToTranslate.forEach((item, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          updatedWords[item.index].englishMeaning = translatedText;
        }
      });
      
      setGeneratedWords(updatedWords);
    } catch (error) {

      alert('Failed to translate words: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  // Validation function
  const validate = () => {
    const errors = [];
    
    if (!content.words || content.words.length === 0) {
      errors.push('At least one word pair is required');
    }

    content.words.forEach((word, index) => {
      if (!word.languageWord?.trim()) {
        errors.push(`Language word is required for word ${index + 1}`);
      }
      if (!word.languageMeaning?.trim()) {
        errors.push(`Language meaning is required for word ${index + 1}`);
      }
      if (!word.englishMeaning?.trim()) {
        errors.push(`English meaning is required for word ${index + 1}`);
      }
    });

    return errors;
  };

  return (
    <div className="space-y-6">
      {/* Words Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Word Pairs</h3>
            <p className="text-sm text-gray-600 mt-1">{content.words.length} word{content.words.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm"
              onClick={() => setShowImageToTextPopup(true)}
            >
              <FaCamera className="w-4 h-4 mr-2" />
              Extract from Image
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addWord}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Word
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm disabled:bg-orange-300 disabled:cursor-not-allowed text-sm"
              onClick={generateAudioForExistingWords}
              disabled={generatingAudio || !content.words || content.words.length === 0}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating Audio...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Missing Audio
                </>
              )}
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {content.words.map((word, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-4">
                {/* Index */}
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>
                
                {/* Text Fields */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Language word *"
                    value={word.languageWord || ''}
                    onChange={(e) => updateWord(index, 'languageWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Language meaning *"
                    value={word.languageMeaning || ''}
                    onChange={(e) => updateWord(index, 'languageMeaning', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="English meaning *"
                    value={word.englishMeaning || ''}
                    onChange={(e) => updateWord(index, 'englishMeaning', e.target.value)}
                  />
                </div>
                
                {/* Audio Files - Compact */}
                <div className="flex items-center gap-2">
                  {/* Language Word Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`kw-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'languageWordAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-languageWordAudio`]}
                    />
                    <label
                      htmlFor={`kw-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="Language Word Audio"
                    >
                      🎵 KW
                    </label>
                    {uploadingFiles[`${index}-languageWordAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.languageWordAudio && !uploadingFiles[`${index}-languageWordAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.languageWordAudio)}
                          title="Play Language Word Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'languageWordAudio')}
                          title="Remove Language Word Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Language Meaning Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`km-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'languageMeaningAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-languageMeaningAudio`]}
                    />
                    <label
                      htmlFor={`km-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="Language Meaning Audio"
                    >
                      🎵 KM
                    </label>
                    {uploadingFiles[`${index}-languageMeaningAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.languageMeaningAudio && !uploadingFiles[`${index}-languageMeaningAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.languageMeaningAudio)}
                          title="Play Language Meaning Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'languageMeaningAudio')}
                          title="Remove Language Meaning Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* English Meaning Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`em-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'englishMeaningAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-englishMeaningAudio`]}
                    />
                    <label
                      htmlFor={`em-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="English Meaning Audio"
                    >
                      🎵 EM
                    </label>
                    {uploadingFiles[`${index}-englishMeaningAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.englishMeaningAudio && !uploadingFiles[`${index}-englishMeaningAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.englishMeaningAudio)}
                          title="Play English Meaning Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'englishMeaningAudio')}
                          title="Remove English Meaning Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Image - Compact */}
                <div className="flex items-center gap-1">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id={`image-${index}`}
                    onChange={(e) => handleFileUpload(index, 'image', e.target.files[0])}
                    disabled={uploadingFiles[`${index}-image`]}
                  />
                  <label
                    htmlFor={`image-${index}`}
                    className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                    title="Upload Image"
                  >
                    🖼️ IMG
                  </label>
                  {uploadingFiles[`${index}-image`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                  {word.image && !uploadingFiles[`${index}-image`] && (
                    <>
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={async () => {
                          try {
                            const storage = getStorage();
                            const imageRef = ref(storage, word.image);
                            const downloadURL = await getDownloadURL(imageRef);
                            window.open(downloadURL, '_blank');
                          } catch (error) {

                            alert(`Failed to preview image. Error: ${error.message}\nPath: ${word.image}`);
                          }
                        }}
                        title="Preview Image"
                      >
                        <FaEye className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => removeFile(index, 'image')}
                        title="Remove Image"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                
                {/* Confusing Letters - Compact */}
                <input
                  type="text"
                  className="w-32 px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Confusing letters"
                  value={word.confusingLetters?.join(' ') || ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    const letters = input.split(/[\s,]+/).filter(letter => letter.trim() !== '');
                    updateWord(index, 'confusingLetters', letters);
                  }}
                  title={`${word.confusingLetters?.length || 0} letters • Separate with spaces`}
                />
                
                {/* Remove Button */}
                {content.words.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeWord(index)}
                    title="Remove Word"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image-to-Text Popup */}
      {showImageToTextPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Extract Words from Image</h3>
                <p className="text-sm text-gray-600 mt-1">Upload an image to automatically extract word pairs</p>
              </div>
              <button
                onClick={closeImageToTextPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {!uploadedImage ? (
                /* Upload Section */
                <div className="text-center">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-emerald-400 transition-colors">
                    <div className="flex flex-col items-center">
                      <FaCamera className="w-12 h-12 text-gray-400 mb-4" />
                      <label htmlFor="imageToTextUpload" className="text-lg font-medium text-gray-900 mb-2 cursor-pointer">
                        Upload an image to extract words
                      </label>
                      <input
                        type="file"
                        id="imageToTextUpload"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUploadForText}
                        disabled={imageProcessing}
                      />
                      <button
                        type="button"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:bg-emerald-400 disabled:cursor-not-allowed mb-3"
                        onClick={() => document.getElementById('imageToTextUpload').click()}
                        disabled={imageProcessing}
                      >
                        Choose Image File
                      </button>
                      <p className="text-sm text-gray-500">
                        Supported formats: JPG, PNG, GIF. The AI will analyze the image and extract word pairs.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Processing and Results Section */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Uploaded Image */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FaImage className="text-blue-600" />
                        Uploaded Image
                      </h4>
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <img 
                          src={imageBlobUrl} 
                          alt="Uploaded" 
                          className="w-full h-auto rounded-lg shadow-sm"
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                        />
                      </div>
                      <button 
                        className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300 transition-colors flex items-center justify-center gap-2 text-sm"
                        onClick={() => {
                          closeImageToTextPopup();
                        }}
                      >
                        <FaUpload className="w-4 h-4" />
                        Upload Different Image
                      </button>
                    </div>
                  </div>
                  
                  {/* Generated Words */}
                  <div className="lg:col-span-2">
                    {imageProcessing ? (
                      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <div className="flex flex-col items-center gap-4">
                          <FaSpinner className="w-12 h-12 animate-spin text-emerald-500" />
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">Processing Image</h4>
                            <p className="text-gray-600 text-sm">Analyzing image and extracting words using AI...</p>
                          </div>
                        </div>
                      </div>
                    ) : generatedWords.length > 0 ? (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                          <div>
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              ✨ Generated words ({generatedWords.length})
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={translateLanguageWords}
                              disabled={translating || generatingAudio}
                            >
                              {translating ? (
                                <>
                                  <FaSpinner className="w-4 h-4 animate-spin" />
                                  Translating...
                                </>
                              ) : (
                                <>
                                  🌐 Translate Words
                                </>
                              )}
                            </button>
                            <button 
                              className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={generateAudioForWords}
                              disabled={generatingAudio || translating}
                            >
                              {generatingAudio ? (
                                <>
                                  <FaSpinner className="w-4 h-4 animate-spin" />
                                  Generating Audio...
                                </>
                              ) : (
                                <>
                                  🔊 Generate Audio
                                </>
                              )}
                            </button>
                            <button 
                              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2 text-sm"
                              onClick={addGeneratedWords}
                            >
                              <FaPlus className="w-4 h-4" />
                              Add All Words
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {generatedWords.map((word, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Language Word</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={word.languageWord}
                                    onChange={(e) => editGeneratedWord(index, 'languageWord', e.target.value)}
                                    placeholder="Language word..."
                                  />
                                  {word.languageWordAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(word.languageWordAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Language Meaning</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={word.languageMeaning}
                                    onChange={(e) => editGeneratedWord(index, 'languageMeaning', e.target.value)}
                                    placeholder="Language meaning..."
                                  />
                                  {word.languageMeaningAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(word.languageMeaningAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">English Meaning</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={word.englishMeaning}
                                    onChange={(e) => editGeneratedWord(index, 'englishMeaning', e.target.value)}
                                    placeholder="English meaning..."
                                  />
                                  {word.englishMeaningAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(word.englishMeaningAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-end gap-1">
                                <button 
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                  onClick={() => addRowAbove(index)}
                                  title="Add row above this word"
                                >
                                  <FaPlus className="w-4 h-4" />
                                </button>
                                <button 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  onClick={() => removeGeneratedWord(index)}
                                  title="Remove this word"
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <span className="text-4xl">📝</span>
                          <div>
                            <h4 className="text-lg font-semibold text-blue-900 mb-1">No words generated</h4>
                            <p className="text-blue-700 text-sm">The image processing didn&apos;t generate words. Try uploading a different image with clearer text.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Validation Display */}
      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

// Export validation function separately for use in parent component
export const validateWordMeaningContent = (content) => {
  const errors = [];
  
  if (!content.words || content.words.length === 0) {
    errors.push('At least one word pair is required');
  }

  content.words?.forEach((word, index) => {
    if (!word.languageWord?.trim()) {
      errors.push(`Language word is required for word ${index + 1}`);
    }
    if (!word.languageMeaning?.trim()) {
      errors.push(`Language meaning is required for word ${index + 1}`);
    }
    if (!word.englishMeaning?.trim()) {
      errors.push(`English meaning is required for word ${index + 1}`);
    }
    
    // Validate English meaning: must start with capital, should not end with period
    if (word.englishMeaning?.trim()) {
      const trimmed = word.englishMeaning.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`English meaning for word ${index + 1} must start with a capital letter`);
      }
      if (/\.$/.test(trimmed)) {
        errors.push(`English meaning for word ${index + 1} should not end with a period`);
      }
    }
  });

  return errors;
};

export default WordMeaningActivity;
